<p-drawer
  [(visible)]="tasksSidebarVisible"
  position="right"
  [style]="{ width: '60em', 'max-width': '100%' }"
  class="tasks-sidebar"
  (onHide)="onHide()"
>
  <ng-template #header>
    <div class="flex">
      <h3 class="m-0 mb-4 flex justify-start gap-1 p-0 text-2xl font-bold">
        {{ 'reportTasks.sidebarTitle' | translate | titlecase }}
      </h3>
      <button
        pButton
        type="button"
        (click)="closeAllTaskSockets()"
        [text]="true"
        severity="secondary"
        class="border-none bg-transparent text-[#495057]"
        iconOnly
      >
        <fa-icon [icon]="faArrowsRotate"></fa-icon>
      </button>
    </div>
  </ng-template>

  <section>
    <header class="mb-4 flex items-center justify-between">
      <h4 class="m-0 p-0 text-xl font-bold">
        {{ ('reportTasks.pendingTasks' | translate | titlecase) + ' (' + (pendingTasks$ | async)?.length + ')' }}
      </h4>
      <div class="flex items-center justify-end"></div>
    </header>
    <app-agnostic-grids [dependencies]="(pendingTasksDependencies$ | async)!"></app-agnostic-grids>
  </section>

  <section>
    <header class="mt-4 mb-4 flex items-center justify-between">
      <h4 class="m-0 p-0 text-xl font-bold">
        {{ ('reportTasks.finishedTasks' | translate | titlecase) + ' (' + (finishedTasks$ | async)?.length + ')' }}
      </h4>
      <div class="flex items-center justify-end gap-2">
        <button
          pButton
          type="button"
          severity="danger"
          (click)="onDelete(selectedTasks)"
          [disabled]="this.selectedTasks.length <= 0"
        >
          <fa-icon [icon]="faTrash"></fa-icon>
          <span>{{ ('buttons.deleteSingle' | translate) + ' (' + this.selectedTasks.length + ')' }}</span>
        </button>
        <button
          pButton
          type="button"
          (click)="onShare(selectedTasks)"
          [disabled]="shareButtonDisabled(this.selectedTasks)"
        >
          <fa-icon [icon]="faShare"></fa-icon>
          <span>{{ ('buttons.share' | translate) + ' (' + this.selectedTasks.length + ')' }}</span>
        </button>
        <button
          pButton
          type="button"
          (click)="onDownload(selectedTasks)"
          [disabled]="downloadButtonDisabled(this.selectedTasks)"
        >
          <fa-icon [icon]="faDownload"></fa-icon>
          <span>
            {{ ('buttons.download' | translate) + ' (' + downloadButtonTaskLength(this.selectedTasks) + ')' }}
          </span>
        </button>
      </div>
    </header>
    <app-agnostic-grids
      [dependencies]="(finishedTasksDependencies$ | async)!"
      (selectionChange)="onSelectionChange($event)"
      (gridApiChange)="onGridApiChange($event)"
    ></app-agnostic-grids>
  </section>
</p-drawer>

<p-dialog
  [header]="'reportTasks.shareDialog.title' | translate"
  [modal]="true"
  [(visible)]="shareDialogVisible"
  [style]="{ width: '50rem', minHeight: '60vh', maxHeight: '95vh' }"
  [draggable]="false"
  (onHide)="onHideShareDialog()"
>
  <p-divider type="solid">
    <b>{{ 'reportTasks.shareDialog.selectedTasks' | translate }} ({{ selectedTasks.length }})</b>
  </p-divider>

  <ul class="m-0 flex w-full flex-col items-center justify-center gap-4">
    @for (task of firstThreeSelectedTasks(); track $index) {
      <li class="flex w-full list-none items-center justify-center gap-2">
        {{ task.reportName }}
        <p-tag
          [ngClass]="{
            'bg-(--p-tag-danger-background) text-(--p-tag-danger-color)': task.type === TaskReportType.STRATEGY,
            'bg-(--p-tag-lime-background) text-(--p-tag-lime-color)': task.type === TaskReportType.INSPECTION,
            'bg-(--p-tag-info-background) text-(--p-tag-info-color)': task.type === TaskReportType.SCENARIO,
            'bg-(--p-tag-amber-background) text-(--p-tag-amber-color)': task.type === TaskReportType.DATA_ANALYSIS,
          }"
        >
          @switch (task.type) {
            @case (TaskReportType.STRATEGY) {
              {{ 'reportTasks.types.strategy' | translate }}
            }
            @case (TaskReportType.INSPECTION) {
              {{ 'reportTasks.types.inspection' | translate }}
            }
            @case (TaskReportType.SCENARIO) {
              {{ 'reportTasks.types.scenario' | translate }}
            }
            @case (TaskReportType.DATA_ANALYSIS) {
              {{ 'reportTasks.types.dataAnalysis' | translate }}
            }
          }
        </p-tag>
      </li>
    }
    @if (selectedTasks.length > 3) {
      <li class="list-none">...</li>
    }
  </ul>
  <p-divider type="solid">
    <b>{{ 'reportTasks.shareDialog.emails' | translate }}</b>
  </p-divider>

  <form [formGroup]="shareEmailFormGroup" class="flex flex-col gap-4 pt-4">
    <b>{{ 'fields.description' | translate }}</b>
    <quill-editor id="share-email-description" class="quill-editor w-full" formControlName="description">
      <div quill-editor-toolbar class="flex flex-wrap items-center gap-x-1">
        <span class="ql-formats">
          <button class="ql-bold" aria-label="Bold"></button>
          <button class="ql-italic" aria-label="Italic"></button>
          <button class="ql-underline" aria-label="Underline"></button>
          <button class="ql-strike" aria-label="Strikethrough"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered" aria-label="Ordered list"></button>
          <button class="ql-list" value="bullet" aria-label="Bullet list"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-script" value="sub" aria-label="Subscript"></button>
          <button class="ql-script" value="super" aria-label="Superscript"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-color" aria-label="Text color"></select>
          <select class="ql-background" aria-label="Background color"></select>
        </span>
        @if (documentCodesResource.value().length > 0) {
          <span class="ql-formats mr-0! ml-auto">
            <p-select
              #documentCodeSelect
              appendTo="body"
              class="w-45!"
              [options]="documentCodesResource.value()"
              [loading]="documentCodesResource.isLoading()"
              optionLabel="naam"
              [placeholder]="'buttons.templates' | translate"
              (onChange)="onSelectDocumentCode($event.value, documentCodeSelect)"
            >
              <ng-template #loadingicon>
                <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
              </ng-template>
            </p-select>
          </span>
        }
      </div>
    </quill-editor>
    <p-inputgroup>
      <p-floatlabel variant="on">
        <input id="share-email" pInputText [formControl]="emailFormControl" [invalid]="isInvalid(emailFormControl)" />
        <label for="share-email">{{ 'fields.email' | translate }}</label>
      </p-floatlabel>

      <p-inputgroup-addon>
        <button pButton type="button" (click)="onAddEmail()" iconOnly>
          <fa-icon [icon]="faPlus"></fa-icon>
        </button>
      </p-inputgroup-addon>
    </p-inputgroup>

    @if (shareEmailFormGroup.controls.emails.value.size <= 0) {
      <p-message severity="info" [closable]="false" class="flex justify-center">
        {{ 'reportTasks.shareDialog.emailsInfo' | translate }}
      </p-message>
    } @else {
      <div class="flex flex-wrap gap-2 pt-2">
        @for (email of shareEmailFormGroup.controls.emails.value; track $index; let i = $index) {
          <p-chip
            [label]="email"
            [removable]="i === shareEmailFormGroup.controls.emails.value.size - 1"
            (onRemove)="onRemoveEmail(email)"
          />
        }
      </div>
    }
  </form>
  <ng-template #footer>
    <div class="flex w-full justify-between gap-2 pt-2">
      <button pButton type="button" severity="secondary" (click)="onHideShareDialog()">
        <fa-icon [icon]="faTimes"></fa-icon>
        <span>{{ 'buttons.cancel' | translate }}</span>
      </button>
      <button
        pButton
        type="button"
        severity="primary"
        (click)="onSubmitShareTasks()"
        [disabled]="shareEmailFormGroup.invalid || loadingShareTasks()"
      >
        @if (loadingShareTasks()) {
          <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
        } @else {
          <fa-icon [icon]="faShare"></fa-icon>
        }
        <span>{{ 'buttons.share' | translate }}</span>
      </button>
    </div>
  </ng-template>
</p-dialog>
